/**
 * Device tier: 0 = lowest (no bloom, no particles, analytic-only extras), 1 = mid, 2 = full.
 * Decided once at mount from a ~150 ms micro-benchmark of the main shader at 512² plus device hints.
 */

export type Tier = 0 | 1 | 2;

export interface TierInputs {
  /** measured milliseconds for one 512×512 draw of the main shader (Infinity/NaN if the benchmark failed) */
  msPer512: number;
  /** navigator.hardwareConcurrency (0 if unknown) */
  concurrency: number;
  /** navigator.deviceMemory in GB (undefined if unknown) */
  memoryGB: number | undefined;
  /** full-resolution device pixel count the main pass will actually shade (css area × dpr²) */
  pixels: number;
}

/** Estimated full-frame cost of the main pass in ms at the given pixel count. */
export function estimateFrameMs(msPer512: number, pixels: number): number {
  return msPer512 * (pixels / (512 * 512));
}

export function decideTier(i: TierInputs): Tier {
  const mem = i.memoryGB ?? 8;
  const cores = i.concurrency || 4;
  if (!Number.isFinite(i.msPer512) || i.msPer512 <= 0) {
    // benchmark failed: fall back to the device hints alone
    if (cores >= 8 && mem >= 8) return 2;
    if (cores <= 2 || mem <= 2) return 0;
    return 1;
  }
  const est = estimateFrameMs(i.msPer512, i.pixels);
  if (est > 12 || cores <= 2 || mem <= 2) return 0;
  if (est <= 5 && cores >= 4 && mem >= 4) return 2;
  return 1;
}

/** Simulation / particle budgets per tier. */
export function tierBudgets(tier: Tier): { rippleSize: number; rippleSteps: number; particles: number; nebulaEvery: number; bloom: boolean } {
  switch (tier) {
    case 2: return { rippleSize: 512, rippleSteps: 2, particles: 1500, nebulaEvery: 1, bloom: true };
    case 1: return { rippleSize: 256, rippleSteps: 1, particles: 600, nebulaEvery: 2, bloom: false };
    default: return { rippleSize: 256, rippleSteps: 1, particles: 0, nebulaEvery: 2, bloom: false };
  }
}

/**
 * Time repeated draws for about `budgetMs`. `draw` must issue one 512² draw of the main shader into a bound FBO;
 * `sync` must force completion (a 1×1 readPixels). Returns ms per draw.
 */
export function runBenchmark(draw: () => void, sync: () => void, now: () => number, budgetMs = 150): number {
  // warm-up: shader compile / first-use costs must not count
  draw(); sync();
  const start = now();
  let draws = 0;
  const batch = 4;
  while (now() - start < budgetMs) {
    for (let i = 0; i < batch; i++) draw();
    sync();
    draws += batch;
  }
  const elapsed = now() - start;
  return draws > 0 ? elapsed / draws : Infinity;
}

/** DPR cap per PLAN §4.4: 1.5 on phones, 2 elsewhere. */
export function dprCapFor(isPhone: boolean): number {
  return isPhone ? 1.5 : 2;
}

/**
 * Adaptive DPR step (PLAN §4.4): step down 0.25 when the 1 s rolling average frame time exceeds 20 ms;
 * step back up 0.25 when it has been comfortably fast (<11 ms) for a while. Never above `cap`, never below 0.75.
 */
export function nextDprScale(current: number, avgFrameMs: number, cap: number, fastSeconds: number): number {
  const min = Math.min(0.75, cap);
  if (avgFrameMs > 20) return Math.max(min, current - 0.25);
  if (avgFrameMs < 11 && fastSeconds >= 5) return Math.min(cap, current + 0.25);
  return current;
}

export function detectPhone(nav: { userAgent?: string; maxTouchPoints?: number; userAgentData?: { mobile?: boolean } } = navigator, screenMin = Math.min(screen.width, screen.height)): boolean {
  if (nav.userAgentData?.mobile !== undefined) return nav.userAgentData.mobile;
  const ua = nav.userAgent ?? '';
  if (/iPhone|iPod|Android.*Mobile|Mobile Safari/i.test(ua)) return true;
  return (nav.maxTouchPoints ?? 0) > 1 && screenMin < 700;
}
